import React, { useState, useEffect } from "react";
import { createCategory, updateCategory } from "./adminDailySentenceService";
import "../../styles/content.css";

const CATEGORY_KEYS = [
  // Everyday Essentials (1–16)
  "GREETINGS", "SHOPPING", "RESTAURANT", "TRANSPORTATION",
  "DAILY_ACTIVITIES", "WEATHER", "WORK_OFFICE", "HEALTH_MEDICAL",
  "FAMILY_FRIENDS", "TRAVEL_TOURISM", "EDUCATION_LEARNING", "EMERGENCY_HELP",
  "TIME_DATES", "DIRECTIONS_LOCATIONS", "PHONE_COMMUNICATION", "HOBBIES_INTERESTS",

  // Social Basics (17–27)
  "INTRODUCTIONS", "SMALL_TALK", "FAREWELLS", "COMPLIMENTS",
  "APOLOGIES", "THANKING", "REQUESTS", "OPINIONS",
  "FEELINGS", "ENCOURAGEMENT", "INVITATIONS",

  // Daily Life (28–38)
  "DAILY_ROUTINE", "DIRECTIONS", "TIME", "FAMILY",
  "FRIENDS", "HOBBIES", "FOOD_AND_DRINK", "HEALTH",
  "FITNESS", "CLOTHING", "HOME",

  // Places & Services (39–47)
  "HOSPITAL", "PHARMACY", "BANK", "POST_OFFICE",
  "SALON", "GYM", "SUPERMARKET", "LIBRARY", "POLICE_STATION",

  // Travel (48–58)
  "TRAVEL", "AIRPORT", "HOTEL", "TRANSPORT",
  "BUS", "TRAIN", "TAXI", "CAR_RENTAL",
  "IMMIGRATION", "SIGHTSEEING", "BOOKING",

  // Professional (59–68)
  "WORKPLACE", "SCHOOL", "COLLEGE", "INTERVIEWS",
  "MEETINGS", "PRESENTATIONS", "EMAILS", "NETWORKING",
  "JOB_SEARCH", "OFFICE_TALK",

  // Communication (69–72)
  "PHONE_CALLS", "VIDEO_CALLS", "TEXTING", "SOCIAL_MEDIA",

  // Situational (73–83)
  "EMERGENCIES", "COMPLAINTS", "NEGOTIATIONS", "ARGUMENTS",
  "ADVICE", "SUGGESTIONS", "PERMISSION", "PROMISES",
  "CELEBRATIONS", "CONDOLENCES", "ANNOUNCEMENTS",

  // Real-Life Scenarios (84–92)
  "AT_THE_DOCTOR", "AT_THE_DENTIST", "AT_THE_MECHANIC",
  "ORDERING_FOOD", "ASKING_FOR_HELP", "MAKING_PLANS",
  "GIVING_DIRECTIONS", "TELLING_STORIES", "EXPRESSING_LOVE",
];

const DailySentenceCategoryForm = ({ category, onSaved, onCancel }) => {
  const isEdit = !!category;
  const [form, setForm] = useState({
    category: "", title: "", description: "", icon: "",
    youtubeVideoUrl: "", videoTitle: "", videoDescription: "",
    sortOrder: 1, isActive: true,
  });
  const [useCustomKey, setUseCustomKey] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category) {
      console.log("[DailySentenceCategoryForm] Populating for edit, id:", category.id, "key:", category.category);
      const key = category.category || "";
      setForm({
        category: key,
        title: category.title || "",
        description: category.description || "",
        icon: category.icon || "",
        youtubeVideoUrl: category.youtubeVideoUrl || "",
        videoTitle: category.videoTitle || "",
        videoDescription: category.videoDescription || "",
        sortOrder: category.sortOrder || 1,
        isActive: category.isActive !== false,
      });
      if (key && !CATEGORY_KEYS.includes(key)) {
        console.log("[DailySentenceCategoryForm] Key", key, "not in predefined list, switching to custom input");
        setUseCustomKey(true);
      }
    }
  }, [category]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log("[DailySentenceCategoryForm] Field changed:", name, "=>", type === "checkbox" ? checked : value);
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async () => {
    if (!form.category || !form.title) {
      console.warn("[DailySentenceCategoryForm] Validation failed");
      alert("Category key and Title are required");
      return;
    }
    const payload = {
      ...form,
      sortOrder: Number(form.sortOrder) || 1,
    };
    console.log("[DailySentenceCategoryForm] Submitting", isEdit ? "update" : "create", "payload:", payload);
    setLoading(true);
    const res = isEdit
      ? await updateCategory(category.id, payload)
      : await createCategory(payload);
    if (res.status === 200 || res.status === 201) {
      console.log("[DailySentenceCategoryForm] Submit successful:", res.status);
      onSaved();
    } else {
      console.error("[DailySentenceCategoryForm] Submit failed:", res.status, res.message);
      alert(res.message || "Operation failed");
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 560, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Create"} Category</h2>

          <div className="form-group">
            <label>Category Key *</label>
            {isEdit ? (
              <input value={form.category} disabled />
            ) : useCustomKey ? (
              <>
                <input
                  name="category" placeholder="e.g. MY_CUSTOM_KEY"
                  value={form.category} onChange={(e) => {
                    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "");
                    console.log("[DailySentenceCategoryForm] Custom key input:", val);
                    setForm({ ...form, category: val });
                  }}
                />
                <button type="button" onClick={() => { console.log("[DailySentenceCategoryForm] Switching to dropdown mode"); setUseCustomKey(false); setForm({ ...form, category: "" }); }}
                  style={{ marginTop: 4, fontSize: 12, background: "none", border: "none", color: "#019095", cursor: "pointer" }}>
                  ← Pick from list
                </button>
              </>
            ) : (
              <>
                <select name="category" value={form.category} onChange={handleChange}>
                  <option value="">Select Category Key</option>
                  {CATEGORY_KEYS.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
                <button type="button" onClick={() => { console.log("[DailySentenceCategoryForm] Switching to custom key mode"); setUseCustomKey(true); }}
                  style={{ marginTop: 4, fontSize: 12, background: "none", border: "none", color: "#019095", cursor: "pointer" }}>
                  + Enter custom key
                </button>
              </>
            )}
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input name="title" placeholder="Greetings & Introductions" value={form.title} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" placeholder="Brief description..." value={form.description} onChange={handleChange} rows={2} />
          </div>
          <div className="form-group">
            <label>Icon (emoji)</label>
            <input name="icon" placeholder="👋" value={form.icon} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>YouTube Video URL</label>
            <input name="youtubeVideoUrl" placeholder="https://youtube.com/watch?v=..." value={form.youtubeVideoUrl} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Video Title</label>
            <input name="videoTitle" placeholder="Master English Greetings" value={form.videoTitle} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Video Description</label>
            <textarea name="videoDescription" placeholder="Video description..." value={form.videoDescription} onChange={handleChange} rows={2} />
          </div>
          <div className="form-group">
            <label>Sort Order</label>
            <input name="sortOrder" type="number" min="1" value={form.sortOrder} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input name="isActive" type="checkbox" checked={form.isActive} onChange={handleChange} />
              Active
            </label>
          </div>

          <div className="form-actions">
            <button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailySentenceCategoryForm;
